import { IScope } from 'angular';
import { StateParams, StateService } from 'angular-ui-router';
import activityReportTemplate from './activityReport.html';

type ActivityReportGroup = {
  month: number;
  year: number;
  formattedDate: string;
  records: any[];
  durationByMember: { [key: string]: number };
  total: number;
};

const ALL = 'all';

class ActivityReportsController {
  static $inject = [
    '$scope',
    '$state',
    '$stateParams',
    '$location',
    '$filter',
    'Activity',
    'ActivityReport',
    'PublicIdService',
    'AuthService',
  ];

  sections: { [key: string]: boolean } = {};
  authenticated: boolean;
  activities: string[] = [];
  selectedActivityName: string = "";
  result: {
    groups: ActivityReportGroup[];
    durationByMember: { [key: string]: number };
    total: number;
  } | null = null;
  members: any[] = [];
  activity: any;

  constructor(
    private $scope: IScope,
    private $state: StateService,
    private $stateParams: StateParams,
    private $location: angular.ILocationService,
    private $filter: angular.IFilterService,
    private Activity,
    private ActivityReport,
    private PublicIdService,
    private AuthService,
  ) {
    this.authenticated = this.AuthService.isAuthenticated();
  }

  async $onInit() {
    if (this.authenticated) {
      this.Activity.query({ types: 'BILLABLE,FIXED_PRICE,INTERNAL' }).then(
        (activities) => {
          this.activities = activities.map((activity) => activity.name);
        },
      );
    }

    if (this.$stateParams.activity) {
      this.selectedActivityName = decodeURIComponent(
        this.$stateParams.activity,
      );
      this.data(this.selectedActivityName);
    }
  }

  getAccessUrl() {
    return this.$location.absUrl();
  }

  data(activity) {
    if (!activity) {
      this.result = null;
      return;
    }

    const r = this.ActivityReport.get(
      this.PublicIdService.withAccessid(
        { activity: activity },
        this.$stateParams.accessid,
      ),
      () => {
        this.sections = {};
        this.members = r.members;
        this.activity = r.activity;
        this.$scope.$broadcast('headerChanged');

        const result = {
          groups: [] as ActivityReportGroup[],
          durationByMember: {},
          total: 0,
        };

        for (const i in r.members) {
          result.durationByMember[r.members[i].id] = 0;
        }

        let group: ActivityReportGroup | null = null;
        // group by month
        r.records.forEach((record) => {
          const date = new Date(record.date);

          if (
            group == null ||
            date.getMonth() != group.month ||
            date.getYear() != group.year
          ) {
            // begin a new group
            group = {
              month: date.getMonth(),
              year: date.getYear(),
              formattedDate: this.toMonth(date),
              records: [],
              durationByMember: {},
              total: 0,
            };

            for (const i in r.members) {
              group.durationByMember[r.members[i].id] = 0;
            }

            result.groups.unshift(group);
          }

          record.formattedDate = this.toDate(date);
          group.records.push(record);

          for (const memberId in record.durationByMember) {
            const v = (record.durationByMember[memberId] =
              record.durationByMember[memberId] / 100);
            group.durationByMember[memberId] += v;
            group.total += v;
            result.durationByMember[memberId] += v;
            result.total += v;
          }
        });

        // sort days within months
        result.groups = result.groups.map((group) => {
          group.records = group.records.sort((a, b) => b.date - a.date);

          return group;
        });

        // set result
        this.result = result;
      },
      (err) => {
        if (err.status === 403) {
          this.$state.go('tokenExpired');
        }
      },
    );
  }

  selectActivity(selected) {
    this.$state.go('.', { activity: selected });
  }

  private toKey(group: ActivityReportGroup | null) {
    return group ? group.formattedDate : ALL;
  }

  sectionClass(group: ActivityReportGroup | null) {
    return this.sections[this.toKey(group)]
      ? 'section-opened'
      : 'section-closed';
  }

  toggleSection(group: ActivityReportGroup | null) {
    const key = this.toKey(group);
    const value = !this.sections[key];
    this.sections[key] = value;

    if (this.result) {
      const groups = this.result.groups;

      if (key == ALL) {
        groups.forEach((group) => {
          this.sections[group.formattedDate] = value;
        });
        this.sections[ALL] = value;
      } else {
        let allValue = false;
        groups.forEach((group) => {
          if (this.sections[group.formattedDate]) {
            allValue = true;
          }
        });
        this.sections[ALL] = allValue;
      }
    }
  }

  private toDate(date: Date) {
    return this.$filter('date')(date, 'longDate');
  }

  private toMonth(date: Date) {
    return this.$filter('date')(date, 'MMMM yyyy');
  }

  toDuration(record, member) {
    return (record && record.durationByMember[member.id]) || 0;
  }
}

export const ActivityReportComponent = {
  template: activityReportTemplate,
  controller: ActivityReportsController,
};

